<script setup>
import { ref, reactive, watch, onMounted, computed } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import { useUI } from '@/composables/useUI'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'

import { TP_MOVIMENTO } from '@/constants/TP_MOVIMENTO'

const authStore = useAuthStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(25)
const { obterClasseStatusGhost } = useUI()

const lista = ref([])
const isLoading = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const filtrosAbertos = ref(false)

const filtros = reactive({
  descricao: '',
  lstStatus: ''
})

const listas = reactive({
  status: [],
  tipoMovimento: []
})

// Estado da Auditoria
const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

const emit = defineEmits(['editar-item', 'adicionar-item'])

// RBAC
const podeEditar = computed(() => {
  return authStore.isAdminInterno || (authStore.permissoes && authStore.permissoes.includes('FISCAL_OPERACOES_COMPLETO'))
})

const colunas = [
  { field: 'idOperacao', label: 'ID', width: 80, sortable: true, slotName: 'id' },
  { field: 'descricao', label: 'Descrição', sortable: true, class: 'fw-medium text-body-emphasis' },
  { field: 'lstTipoMovimento', label: 'Tipo Movimento', sortable: true, slotName: 'tipoMovimento' },
  { field: 'geraFinanceiro', label: 'Financeiro', sortable: true, slotName: 'financeiro' },
  { field: 'movimentaEstoque', label: 'Estoque', sortable: true, slotName: 'estoque' },
  { field: 'lstTipoOperacaoEstoque', label: 'Tp. Operação Estoque', sortable: true, slotName: 'tipoOperacao' },
  { field: 'cfopDentroEstado', label: 'CFOP (Dentro UF)', sortable: true },
  { field: 'cfopForaEstado', label: 'CFOP (Fora UF)', sortable: true },
  { field: 'lstStatus', label: 'Status', align: 'center', sortable: true, slotName: 'status' },
  { field: 'acoes', label: 'Ações', width: 120, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

const sortResolver = (field, order, data) => {
  if (!field.startsWith('lst')) return null

  const resolvers = {
    lstTipoMovimento: (row) => getTipoMovimentoTexto(row.lstTipoMovimento),
    lstStatus: (row) => getStatusTexto(row.lstStatus)
  }

  const resolver = resolvers[field]
  if (!resolver) return null

  return [...data].sort((a, b) => {
    const cmp = resolver(a).localeCompare(resolver(b), 'pt-BR')
    return order === 'asc' ? cmp : -cmp
  })
}

const handleSortChange = () => {
  buscarDados(1)
}

async function carregarListas () {
  try {
    const [resStatus, resTipoMovimento, resTipoOpMov] = await Promise.allSettled([
      api.get('/ListasSistema/lookup/ST'),
      api.get('/ListasSistema/lookup/TP_MOVIMENTO'),
      api.get('/ListasSistema/lookup/ESTQ_TIPO_OPERACAO_MOV')
    ])

    if (resStatus.status === 'fulfilled') listas.status = resStatus.value.data
    if (resTipoMovimento.status === 'fulfilled') listas.tipoMovimento = resTipoMovimento.value.data
    if (resTipoOpMov.status === 'fulfilled') listas.ESTQ_TIPO_OPERACAO_MOV = resTipoOpMov.value.data
  } catch (e) {
    console.warn('Erro ao carregar listas auxiliares:', e)
  }
}

function getTipoMovimentoTexto (idTipo) {
  if (!idTipo) return '-'
  const obj = listas.tipoMovimento.find(t => t.idListaSistemaItem === idTipo || t.idListaClienteItem === idTipo)
  return obj ? obj.textoLista : idTipo
}

function getTextoLista (listaKey, id) {
  if (!id) return '-'
  const lista = listas[listaKey]
  if (!lista) return id
  const obj = lista.find(t => String(t.idListaSistemaItem) === String(id) || String(t.idListaClienteItem) === String(id))
  return obj ? obj.textoLista : id
}

function getStatusTexto (idStatus) {
  if (!idStatus) return '-'
  const obj = listas.status.find(s => s.idListaSistemaItem === idStatus || s.idListaClienteItem === idStatus)
  return obj ? obj.textoLista : idStatus
}

async function buscarDados (pageNumber = 1) {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    lista.value = []
    return
  }

  isLoading.value = true
  const params = injetarParametros(pageNumber)

  if (authStore.empresaAtiva) {
    params.append('idEmpresa', authStore.empresaAtiva)
  }

  if (filtros.descricao) params.append('descricao', filtros.descricao)
  if (filtros.lstStatus) params.append('lstStatus', filtros.lstStatus)
  if (sortField.value) params.append('sortField', sortField.value)
  if (sortOrder.value) params.append('sortOrder', sortOrder.value)

  try {
    const response = await api.get(`Operacoes?${params.toString()}`)
    lista.value = response.data
    atualizarPaginacao(response.headers)
  } catch (error) {
    console.error('Erro ao buscar Operações:', error)
  } finally {
    isLoading.value = false
  }
}

function aplicarFiltros () { buscarDados(1) }

function limparFiltros () {
  filtros.descricao = ''
  filtros.lstStatus = ''
  buscarDados(1)
}

function novoItem () {
  if (podeEditar.value) emit('adicionar-item')
}

function editarItem (item) { emit('editar-item', item) }

function abrirAuditoria (item) {
  auditoriaSelecionada.id = item.idOperacao
  auditoriaSelecionada.nome = item.descricao
  mostrarAuditoria.value = true
}

function formatarSimNao (valor) { return valor ? 'Sim' : 'Não' }

function obterClasseTipoMovimento (tipo) {
  return tipo === TP_MOVIMENTO.Entrada ? 'badge bg-success' : 'badge bg-primary'
}

watch(() => authStore.empresaAtiva, async (novaEmpresa) => {
  if (novaEmpresa) {
    await carregarListas()
    buscarDados(1)
  } else {
    lista.value = []
    listas.status = []
    listas.tipoMovimento = []
  }
})

onMounted(async () => {
  if (authStore.empresaAtiva || !authStore.isAdminInterno) {
    await carregarListas()
    buscarDados(1)
  }
})

defineExpose({ buscarDados })
</script>

<template>
  <PaginaAdministrar
    titulo="Operações Fiscais"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    :mostrar-botao-adicionar="podeEditar"
    @mudar-pagina="buscarDados"
    @mudar-tamanho-pagina="buscarDados(1)"
    @adicionar="novoItem"
    @atualizar="aplicarFiltros"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3">
        <div class="col-md-4">
          <input class="form-control" placeholder="Buscar por descrição..." v-model="filtros.descricao" @keyup.enter="aplicarFiltros">
        </div>
        <div class="col-md-3">
          <select class="form-select" v-model="filtros.lstStatus" @change="aplicarFiltros">
            <option value="">Todos os Status</option>
            <option v-for="s in listas.status" :key="s.idListaSistemaItem" :value="s.idListaSistemaItem">{{ s.textoLista }}</option>
          </select>
        </div>
        <div class="col-auto">
          <button class="btn btn-secondary me-2" @click="limparFiltros" :disabled="isLoading">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros" :disabled="isLoading">
            <span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
            Aplicar Filtros
          </button>
        </div>
      </div>
    </template>

    <div v-if="isLoading && lista.length === 0" class="text-center p-5">
      <div class="spinner-border text-primary" role="status"><span class="visually-hidden">A carregar...</span></div>
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="lista"
      :loading="isLoading"
      empty-message="Nenhuma operação encontrada para os filtros aplicados."
      row-key="idOperacao"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editarItem($event.row)"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idOperacao }}</span>
      </template>
      <template #tipoMovimento="{ row }">
        <span :class="obterClasseTipoMovimento(row.lstTipoMovimento)">
          {{ getTipoMovimentoTexto(row.lstTipoMovimento) }}
        </span>
      </template>

      <template #financeiro="{ row }">{{ formatarSimNao(row.geraFinanceiro) }}</template>
      <template #estoque="{ row }">{{ formatarSimNao(row.movimentaEstoque) }}</template>
      <template #tipoOperacao="{ row }">
        <span class="text-muted" v-if="!row.lstTipoOperacaoEstoque">-</span>
        <span class="badge badge-ghost-secondary" v-else>{{ getTextoLista('ESTQ_TIPO_OPERACAO_MOV', row.lstTipoOperacaoEstoque) }}</span>
      </template>

      <template #status="{ row }">
        <span :class="obterClasseStatusGhost(getStatusTexto(row.lstStatus))">
          {{ getStatusTexto(row.lstStatus) }}
        </span>
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1">
          <button class="btn-icon text-secondary" @click="abrirAuditoria(row)" title="Ver Histórico de Alterações">
            <i class="bi bi-clock-history"></i>
          </button>
          <button class="btn-icon text-primary" @click="editarItem(row)" :title="podeEditar ? 'Editar Operação' : 'Visualizar Operação'">
            <i :class="podeEditar ? 'bi bi-pencil-fill' : 'bi bi-eye-fill'"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="Operacao"
    contexto="tenant"
    :chave-primaria="auditoriaSelecionada.id"
    :titulo-registro="auditoriaSelecionada.nome"
    @fechar="mostrarAuditoria = false"
  />
</template>

<style scoped>
</style>
